
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import OpeningBalanceModal from './components/OpeningBalanceModal';

interface Outlet {
  id: string;
  name: string;
  code: string;
  address: string;
}

interface Counter {
  id: string;
  name: string;
  status: 'available' | 'occupied' | 'maintenance';
}

export default function POSCheckIn() {
  const navigate = useNavigate();
  const [selectedOutlet, setSelectedOutlet] = useState<string>('');
  const [selectedCounter, setSelectedCounter] = useState<string>('');
  const [pin, setPin] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [showOpeningBalance, setShowOpeningBalance] = useState(false);
  const [verifiedData, setVerifiedData] = useState<any>(null);
  const [availableCounters, setAvailableCounters] = useState<Counter[]>([]);
  const pinContainerRef = useRef<HTMLDivElement>(null);

  const outlets: Outlet[] = [
    { id: 'OUT001', name: 'Downtown Store', code: 'DT-01', address: '123 Main Street, City Center' },
    { id: 'OUT002', name: 'Mall Branch', code: 'ML-02', address: '456 West Avenue, Mall Plaza' },
    { id: 'OUT003', name: 'Airport Terminal', code: 'AP-03', address: 'Terminal 2, Gate B-12' },
    { id: 'OUT004', name: 'Suburban Center', code: 'SB-04', address: '789 Oak Boulevard, Suburb' },
  ];

  // Generate random counters for selected outlet
  const generateCountersForOutlet = (outletId: string) => {
    const statuses: ('available' | 'occupied' | 'maintenance')[] = ['available', 'occupied', 'maintenance'];
    const counterCount = Math.floor(Math.random() * 4) + 4; // 4-7 counters
    
    const counters: Counter[] = [];
    for (let i = 1; i <= counterCount; i++) {
      const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
      counters.push({
        id: `${outletId}_CNT${String(i).padStart(3, '0')}`,
        name: `Counter ${String(i).padStart(2, '0')}`,
        status: i <= 2 ? 'available' : randomStatus, // Ensure at least 2 available counters
      });
    }
    
    return counters;
  };

  // Handle outlet selection
  const handleOutletSelect = (outletId: string) => {
    setSelectedOutlet(outletId);
    setSelectedCounter('');
    setPin('');
    setError('');
    
    // Generate counters for this outlet
    const counters = generateCountersForOutlet(outletId);
    setAvailableCounters(counters);
  };

  const handleVerify = async () => {
    setError('');

    if (!selectedOutlet || !selectedCounter || pin.length !== 6) {
      setError('Please complete all fields');
      return;
    }

    setIsVerifying(true);

    // Simulate verification
    setTimeout(() => {
      if (pin === '123456') {
        // Store temporary check-in data
        const checkInData = {
          outlet: outlets.find(o => o.id === selectedOutlet),
          counter: availableCounters.find(c => c.id === selectedCounter),
          user: {
            id: 'USR001',
            name: 'Cashier',
            employeeId: 'EMP-001',
            role: 'Cashier'
          },
          timestamp: new Date().toISOString(),
        };
        setVerifiedData(checkInData);
        setIsVerifying(false);
        
        // Show opening balance modal
        setShowOpeningBalance(true);
      } else {
        setError('Invalid PIN. Please try again.');
        setPin('');
        setIsVerifying(false);
      }
    }, 1000);
  };

  const handleOpeningBalanceConfirm = (amount: number, note: string, denominations?: any) => {
    // Store complete check-in data with opening balance
    const completeCheckInData = {
      ...verifiedData,
      openingBalance: {
        amount,
        note,
        denominations,
        timestamp: new Date().toISOString(),
      },
    };
    sessionStorage.setItem('posCheckIn', JSON.stringify(completeCheckInData));
    
    // Navigate to POS checkout
    navigate('/pos-checkout');
  };

  const handleOpeningBalanceCancel = () => {
    // Reset everything and logout
    setShowOpeningBalance(false);
    setVerifiedData(null);
    setSelectedOutlet('');
    setSelectedCounter('');
    setPin('');
    setError('');
    setAvailableCounters([]);
  };

  const handlePinInput = (digit: string) => {
    if (pin.length < 6) {
      setPin(pin + digit);
      setError('');
    }
  };

  const handlePinDelete = () => {
    setPin(pin.slice(0, -1));
    setError('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'border-green-500 bg-green-50';
      case 'occupied':
        return 'border-red-300 bg-red-50';
      case 'maintenance':
        return 'border-amber-300 bg-amber-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  const getStatusDotColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-500';
      case 'occupied':
        return 'bg-red-500';
      case 'maintenance':
        return 'bg-amber-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available':
        return 'Available';
      case 'occupied':
        return 'Occupied';
      case 'maintenance':
        return 'Maintenance';
      default:
        return '';
    }
  };

  // Auto-focus PIN section when outlet and counter are selected
  useEffect(() => {
    if (selectedOutlet && selectedCounter && pinContainerRef.current) {
      pinContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      pinContainerRef.current.focus();
    }
  }, [selectedOutlet, selectedCounter]);

  // Handle Enter key press
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !isVerifying && selectedOutlet && selectedCounter && pin.length === 6) {
        handleVerify();
      }
      
      // Handle number key input (both top row and numpad)
      if (/^[0-9]$/.test(e.key) && pin.length < 6 && selectedCounter) {
        handlePinInput(e.key);
      }
      
      // Handle backspace
      if (e.key === 'Backspace' && pin.length > 0 && selectedCounter) {
        handlePinDelete();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [pin, selectedOutlet, selectedCounter, isVerifying]);

  return (
    <div className="min-h-screen relative p-8">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(https://static.readdy.ai/image/82828e1ff69cacd1e8315694d834201a/02729a0bd7704f1310dd99c4c0c28996.png)' }}
      >
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">POS Check-In</h1>
          <p className="text-gray-600 text-sm">Select your outlet, counter, and verify your identity to begin your shift</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Outlet & Counter Selection */}
          <div className="lg:col-span-2 space-y-6">
            {/* Select Outlet */}
            <div className="bg-white/95 backdrop-blur-sm rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-store-3-line text-xl text-gray-700"></i>
                <h2 className="text-lg font-semibold text-gray-900">Select Outlet</h2>
              </div>
              <div className="space-y-3">
                {outlets.map(outlet => (
                  <button
                    key={outlet.id}
                    onClick={() => handleOutletSelect(outlet.id)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all cursor-pointer ${
                      selectedOutlet === outlet.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-building-line text-lg text-gray-700"></i>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">{outlet.name}</h3>
                          <p className="text-xs text-gray-500">{outlet.address}</p>
                        </div>
                      </div>
                      {selectedOutlet === outlet.id && (
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-sm text-white"></i>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Select Counter - Only show when outlet is selected */}
            {selectedOutlet && (
              <div className="bg-white/95 backdrop-blur-sm rounded-lg p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-computer-line text-xl text-gray-700"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Select Counter</h2>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {availableCounters.map(counter => (
                    <button
                      key={counter.id}
                      onClick={() => counter.status === 'available' && setSelectedCounter(counter.id)}
                      disabled={counter.status !== 'available'}
                      className={`relative p-4 rounded-lg border-2 transition-all ${
                        selectedCounter === counter.id
                          ? 'border-blue-500 bg-blue-50'
                          : counter.status === 'available'
                          ? 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer'
                          : counter.status === 'occupied'
                          ? 'border-red-200 bg-red-50 cursor-not-allowed opacity-60'
                          : 'border-amber-200 bg-amber-50 cursor-not-allowed opacity-60'
                      }`}
                    >
                      {selectedCounter === counter.id && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <i className="ri-check-line text-xs text-white"></i>
                        </div>
                      )}
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          <i className="ri-computer-line text-lg text-gray-700"></i>
                        </div>
                        <div className="text-center">
                          <p className="font-semibold text-gray-900 text-sm">{counter.name}</p>
                          <div className="flex items-center justify-center gap-1 mt-1">
                            <div className={`w-1.5 h-1.5 rounded-full ${getStatusDotColor(counter.status)}`}></div>
                            <span className="text-xs text-gray-600">{getStatusText(counter.status)}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - PIN Entry - Only show when counter is selected */}
          {selectedCounter && (
            <div className="lg:col-span-1">
              <div 
                ref={pinContainerRef}
                tabIndex={-1}
                className="bg-white/95 backdrop-blur-sm rounded-lg p-6 shadow-sm sticky top-8 outline-none"
              >
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-lock-line text-xl text-gray-700"></i>
                  <h2 className="text-lg font-semibold text-gray-900">Enter PIN</h2>
                </div>

                {/* PIN Display */}
                <div className="mb-6">
                  <div className="flex gap-2 justify-center mb-6">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <div
                        key={index}
                        className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-2xl font-bold ${
                          pin.length > index
                            ? 'border-blue-500 bg-blue-50 text-gray-900'
                            : 'border-gray-300 bg-gray-50 text-gray-300'
                        }`}
                      >
                        {pin.length > index ? '•' : ''}
                      </div>
                    ))}
                  </div>

                  {/* Number Pad */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                      <button
                        key={num}
                        onClick={() => handlePinInput(num.toString())}
                        className="h-12 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg font-semibold text-base text-gray-900 transition-colors cursor-pointer"
                      >
                        {num}
                      </button>
                    ))}
                    <div></div>
                    <button
                      onClick={() => handlePinInput('0')}
                      className="h-12 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg font-semibold text-base text-gray-900 transition-colors cursor-pointer"
                    >
                      0
                    </button>
                    <button
                      onClick={handlePinDelete}
                      className="h-12 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                    >
                      <i className="ri-delete-back-2-line text-lg text-gray-700"></i>
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 text-center">Demo PIN: 123456 | Press Enter to verify</p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                    <i className="ri-error-warning-line text-base text-red-600"></i>
                    <p className="text-xs text-red-600 font-medium">{error}</p>
                  </div>
                )}

                {/* Verify Button */}
                <button
                  onClick={handleVerify}
                  disabled={isVerifying || !selectedOutlet || !selectedCounter || pin.length !== 6}
                  className={`w-full py-3 rounded-lg font-semibold text-white transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
                    isVerifying || !selectedOutlet || !selectedCounter || pin.length !== 6
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                  }`}
                >
                  {isVerifying ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <i className="ri-shield-check-line text-lg"></i>
                      Verify &amp; Check-In
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showOpeningBalance && verifiedData && (
        <OpeningBalanceModal
          onConfirm={handleOpeningBalanceConfirm}
          onCancel={handleOpeningBalanceCancel}
          cashierName={verifiedData.user?.name || ''}
          storeName={verifiedData.outlet?.name || ''}
          lane={verifiedData.counter?.name || ''}
        />
      )}
    </div>
  );
}
